import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Flex, Heading, Text, Tabs, TabItem } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import AdminDashboard from './admin/AdminDashboard';
import ParticipantDashboard from './participant/ParticipantDashboard';
import ProfilePage from './ProfilePage';

const Dashboard = ({ user, signOut, initialView }) => {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasParticipantProjects, setHasParticipantProjects] = useState(false);
  const navigate = useNavigate();
  const { projectId } = useParams();

  useEffect(() => {
    determineUserRole();
  }, [user]);

  const determineUserRole = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const attrs = currentUser.attributes || {};

      const customRole = attrs['custom:role'];
      const userProjects = attrs['custom:projects'] || '';

      const hasProjects = userProjects && userProjects.trim().length > 0;
      setHasParticipantProjects(hasProjects);

      const groups = currentUser.signInUserSession?.accessToken?.payload['cognito:groups'] || [];

      const isAdmin = customRole === 'ADMIN' ||
                      customRole === 'admin' ||
                      groups.includes('admin') ||
                      groups.includes('Admin') ||
                      groups.includes('ADMIN');

      setUserRole(isAdmin ? 'admin' : 'participant');
    } catch (error) {
      console.error('Error determining user role:', error);
      setUserRole('participant');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex direction="column" padding="1rem" alignItems="center">
        <Text>Loading dashboard...</Text>
      </Flex>
    );
  }

  if (initialView === 'profile') {
    return (
      <Flex direction="column" padding="1rem">
        <HeaderNav user={user} userRole={userRole} signOut={signOut} navigate={navigate} />
        <ProfilePage onClose={() => navigate('/')} />
      </Flex>
    );
  }

  // Header component for navigation
  const content = (() => {
    if (userRole === 'admin' && hasParticipantProjects) {
      return (
        <Tabs defaultValue="admin" spacing="equal">
          <TabItem value="admin" title="Admin Dashboard">
            <AdminDashboard user={user} projectId={initialView === 'project' ? projectId : null} />
          </TabItem>
          <TabItem value="participant" title="My Projects">
            <ParticipantDashboard user={user} projectId={initialView === 'project' ? projectId : null} />
          </TabItem>
        </Tabs>
      );
    }

    if (userRole === 'admin') {
      return <AdminDashboard user={user} projectId={initialView === 'project' ? projectId : null} />;
    }

    return <ParticipantDashboard user={user} projectId={initialView === 'project' ? projectId : null} />;
  })();

  return (
    <Flex direction="column" padding="1rem">
      <HeaderNav user={user} userRole={userRole} signOut={signOut} navigate={navigate} />
      {content}
    </Flex>
  );
};

const HeaderNav = ({ user, userRole, signOut, navigate }) => (
  <Flex justifyContent="space-between" alignItems="center" marginBottom="1rem" wrap="wrap" gap="0.5rem">
    <Heading level={4} style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
      NumberSelect Dashboard
    </Heading>
    <Flex alignItems="center" gap="1rem" wrap="wrap">
      <Text>
        Welcome, {user.attributes?.name || 'User'} ({user.attributes?.email || user.username})
      </Text>
      <Text fontSize="0.8rem" color="gray">
        ({userRole === 'admin' ? 'Administrator' : 'Participant'})
      </Text>
      <Button
        size="small"
        onClick={() => navigate('/profile')}
      >
        Profile
      </Button>
      <Button onClick={signOut}>Sign Out</Button>
    </Flex>
  </Flex>
);

export default Dashboard;
